import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { DonutChart } from '../../components/charts/DonutChart';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { getCategoryColor } from '../../data/categoryColors';

type ShotId =
  | 'import'
  | 'spend'
  | 'cards'
  | 'wealth'
  | 'grow'
  | 'loan'
  | 'household'
  | 'alerts'
  | 'ai';

const SPEND_SEGMENTS = [
  { id: 'housing', label: 'Housing', value: 2100, color: getCategoryColor('housing') },
  { id: 'groceries', label: 'Groceries', value: 640, color: getCategoryColor('groceries') },
  { id: 'dining', label: 'Dining', value: 380, color: getCategoryColor('dining') },
  { id: 'subscriptions', label: 'Subs', value: 94, color: getCategoryColor('subscriptions') },
];

const WEALTH_ROWS = [
  { label: 'Brokerage', value: '$96,400', width: '38%', color: colors.accent },
  { label: 'Home equity', value: '$212,000', width: '84%', color: '#38BDF8' },
  { label: 'Cash', value: '$18,200', width: '12%', color: '#A78BFA' },
  { label: 'Loans', value: '−$42,400', width: '22%', color: colors.warning },
];

const GROW_BARS = [
  { year: 'Now', amount: '$48k', h: 36 },
  { year: 'Year 5', amount: '$92k', h: 68 },
  { year: 'Year 10', amount: '$196k', h: 120 },
];

export function CapabilityShot({ id, compact = false }: { id: ShotId; compact?: boolean }) {
  switch (id) {
    case 'import':
      return <ImportShot compact={compact} />;
    case 'cards':
      return <CardsShot />;
    case 'spend':
      return (
        <View style={[styles.spendRow, compact && styles.spendCol]}>
          <DonutChart
            segments={SPEND_SEGMENTS}
            size={compact ? 108 : 120}
            centerLabel="May"
            centerValue="$3,214"
            showLegend={false}
          />
          <View style={styles.spendLegend}>
            {SPEND_SEGMENTS.map((seg) => (
              <View key={seg.id} style={styles.legendRow}>
                <View style={[styles.legendDot, { backgroundColor: seg.color }]} />
                <Text style={styles.legendLabel}>{seg.label}</Text>
                <Text style={styles.legendValue}>${seg.value.toLocaleString()}</Text>
              </View>
            ))}
          </View>
        </View>
      );
    case 'wealth':
      return <WealthShot />;
    case 'grow':
      return <GrowShot />;
    case 'loan':
      return <LoanShot />;
    case 'household':
      return <HouseholdShot />;
    case 'alerts':
      return <AlertShot />;
    case 'ai':
      return <AiShot />;
    default:
      return null;
  }
}

function ImportArrow({ down }: { down?: boolean }) {
  const w = down ? 16 : 28;
  const h = down ? 28 : 16;
  return (
    <View style={[styles.arrowCol, down && styles.arrowColDown]} accessibilityElementsHidden>
      <svg width={w} height={h} viewBox={down ? '0 0 16 28' : '0 0 28 16'} aria-hidden>
        <path
          d={down ? 'M8 2v20M3 16l5 7 5-7' : 'M2 8h20M16 3l7 5-7 5'}
          fill="none"
          stroke={colors.accentMuted}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </View>
  );
}

function ImportShot({ compact }: { compact: boolean }) {
  return (
    <View style={styles.importStack}>
      <View style={[styles.importRow, compact && styles.importCol]}>
        <View style={styles.receipt}>
          <Text style={styles.shotKicker} numberOfLines={1}>
            Card / bank statement
          </Text>
          <View style={styles.receiptBar} />
          <View style={[styles.receiptLine, { width: '78%' }]} />
          <View style={[styles.receiptLine, { width: '54%' }]} />
          <Text style={styles.receiptTotal}>Visa · May</Text>
        </View>
        <ImportArrow down={compact} />
        <View style={styles.chipCol}>
          <Text style={styles.shotKicker} numberOfLines={1}>
            Transactions
          </Text>
          <Chip label="Dining · $186" color={getCategoryColor('dining')} />
          <Chip label="Groceries · $142" color={getCategoryColor('groceries')} />
          <Chip label="Transport · $100" color={getCategoryColor('transport')} />
        </View>
      </View>
      <View style={[styles.importRow, compact && styles.importCol]}>
        <View style={styles.receipt}>
          <Text style={styles.shotKicker} numberOfLines={1}>
            Brokerage screenshot
          </Text>
          <View style={styles.receiptBar} />
          <View style={[styles.receiptLine, { width: '62%' }]} />
          <Text style={styles.receiptTotal}>Fidelity · positions</Text>
        </View>
        <ImportArrow down={compact} />
        <View style={styles.chipCol}>
          <Text style={styles.shotKicker} numberOfLines={1}>
            Portfolio
          </Text>
          <Chip label="VOO · $41,200" color={getCategoryColor('investment')} />
          <Chip label="AAPL · $12,840" color="#38BDF8" />
          <Chip label="Cash · $3,110" color={getCategoryColor('salary')} />
        </View>
      </View>
    </View>
  );
}

function Chip({ label, color }: { label: string; color: string }) {
  return (
    <View style={[styles.chip, { borderColor: `${color}66`, backgroundColor: `${color}22` }]}>
      <View style={[styles.chipDot, { backgroundColor: color }]} />
      <Text style={styles.chipText} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

function WealthShot() {
  return (
    <View style={styles.stack}>
      <Text style={styles.shotKicker}>Net worth today</Text>
      <Text style={styles.heroFigure}>$284,200</Text>
      <Text style={styles.heroSub}>$326,600 assets · $42,400 liabilities</Text>
      {WEALTH_ROWS.map((row) => (
        <View key={row.label} style={styles.holdRow}>
          <Text style={styles.holdLabel}>{row.label}</Text>
          <View style={styles.holdTrack}>
            <View
              style={[styles.holdFill, { width: row.width, backgroundColor: row.color }]}
            />
          </View>
          <Text style={styles.holdValue}>{row.value}</Text>
        </View>
      ))}
    </View>
  );
}

function GrowShot() {
  return (
    <View style={styles.stack}>
      <Text style={styles.shotKicker}>$500 / month · 8% return</Text>
      <View style={styles.growBars}>
        {GROW_BARS.map((bar) => (
          <View key={bar.year} style={styles.growCol}>
            <Text style={styles.growAmount}>{bar.amount}</Text>
            <View style={[styles.growBar, { height: bar.h }]} />
            <Text style={styles.growYear}>{bar.year}</Text>
          </View>
        ))}
      </View>
      <Text style={styles.caption}>Same contribution. Compounding does the later jump.</Text>
    </View>
  );
}

function LoanShot() {
  return (
    <View style={styles.stack}>
      <Text style={styles.shotKicker}>$310,000 remaining</Text>
      <svg
        width="100%"
        height={88}
        viewBox="0 0 240 88"
        role="img"
        aria-label="Loan payoff: minimum versus extra 200 dollars"
      >
        <line x1="8" y1="76" x2="232" y2="76" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        <path
          d="M8 12 C 80 28, 150 48, 228 74"
          fill="none"
          stroke={colors.warning}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M8 12 C 70 36, 120 62, 168 74"
          fill="none"
          stroke={colors.accent}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="228" cy="74" r="3.5" fill={colors.warning} />
        <circle cx="168" cy="74" r="3.5" fill={colors.accent} />
      </svg>
      <View style={styles.loanLegend}>
        <View style={styles.legendRow}>
          <View style={[styles.legendDot, { backgroundColor: colors.warning }]} />
          <Text style={styles.legendLabel}>Minimum</Text>
          <Text style={styles.legendValue}>18 years</Text>
        </View>
        <View style={styles.legendRow}>
          <View style={[styles.legendDot, { backgroundColor: colors.accent }]} />
          <Text style={styles.legendLabel}>Extra $200 / mo</Text>
          <Text style={styles.legendValue}>11 years</Text>
        </View>
      </View>
    </View>
  );
}

function HouseholdShot() {
  return (
    <View style={styles.stack}>
      <View style={styles.toggle}>
        <View style={styles.toggleOff}>
          <Text style={styles.toggleOffText}>Mine</Text>
        </View>
        <View style={styles.toggleOn}>
          <Text style={styles.toggleOnText}>Household</Text>
        </View>
      </View>
      <View style={styles.hhPair}>
        <View style={styles.hhCard}>
          <Text style={styles.shotKicker}>Alex · mine</Text>
          <Text style={styles.hhValue}>$6,240</Text>
          <Text style={styles.caption}>This month</Text>
        </View>
        <View style={[styles.hhCard, styles.hhCardFocus]}>
          <Text style={styles.shotKicker}>Alex + Jordan</Text>
          <Text style={[styles.hhValue, styles.hhValueFocus]}>$11,180</Text>
          <Text style={styles.caption}>Combined</Text>
        </View>
      </View>
    </View>
  );
}

function CardsShot() {
  return (
    <View style={styles.stack}>
      <Text style={styles.shotKicker}>Cards this month</Text>
      <View style={styles.cardTotals}>
        <View style={styles.cardTotalCell}>
          <Text style={styles.formLabel}>Spent</Text>
          <Text style={styles.cardTotalValue}>$1,880</Text>
        </View>
        <View style={styles.cardTotalCell}>
          <Text style={styles.formLabel}>Pay in full</Text>
          <Text style={[styles.cardTotalValue, styles.cardDueValue]}>$2,030</Text>
        </View>
        <View style={styles.cardTotalCell}>
          <Text style={styles.formLabel}>Minimum</Text>
          <Text style={styles.cardTotalValue}>$47</Text>
        </View>
      </View>
      <View style={styles.cardRow}>
        <View style={styles.cardRowMain}>
          <Text style={styles.cardName}>Chase Sapphire · 4242</Text>
          <Text style={[styles.caption, styles.cardDueSoon]}>Credit · Due May 25</Text>
          <Text style={styles.caption}>Spent $1,240 · Full $1,339</Text>
        </View>
        <Text style={styles.channelOn}>Alert on</Text>
      </View>
      <View style={styles.cardRow}>
        <View style={styles.cardRowMain}>
          <Text style={styles.cardName}>Amex Gold · 1008</Text>
          <Text style={styles.caption}>Credit · Due May 25</Text>
          <Text style={styles.caption}>Spent $640 · Full $691</Text>
        </View>
        <Text style={styles.channelOff}>Set alert</Text>
      </View>
    </View>
  );
}

function AlertShot() {
  return (
    <View style={styles.stack}>
      <Text style={styles.shotKicker}>Set a spend alert</Text>
      <View style={styles.formRow}>
        <Text style={styles.formLabel}>Category</Text>
        <Text style={styles.formValue}>Dining</Text>
      </View>
      <View style={styles.formRow}>
        <Text style={styles.formLabel}>Cap</Text>
        <Text style={styles.formValue}>$400 / week</Text>
      </View>
      <View style={styles.formRow}>
        <Text style={styles.formLabel}>Notify</Text>
        <View style={styles.channelRow}>
          <Text style={styles.channelOn}>Email</Text>
          <Text style={styles.channelOn}>Push</Text>
        </View>
      </View>
      <Text style={styles.caption}>You pick when and how — not just a default threshold.</Text>
    </View>
  );
}

function AiShot() {
  return (
    <View style={styles.stack}>
      <View style={styles.aiTeaser}>
        <Text style={styles.shotKicker}>On the dashboard</Text>
        <Text style={styles.aiLine}>Dining is 18% above your usual May pace.</Text>
      </View>
      <View style={styles.chatYou}>
        <Text style={styles.chatYouText}>What’s driving it?</Text>
      </View>
      <View style={styles.chatAi}>
        <Text style={styles.chatAiText}>
          Four weekend dinners at $86 avg. Skip two and you’re back on pace this month.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  spendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    minHeight: 148,
    padding: spacing.sm,
  },
  spendCol: {
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 0,
  },
  spendLegend: {
    flex: 1,
    gap: 6,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  legendLabel: {
    ...typography.caption,
    color: colors.textMuted,
    flex: 1,
  },
  legendValue: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  importStack: {
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  importRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    minWidth: 0,
  },
  importCol: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing.sm,
  },
  receipt: {
    flex: 1,
    minWidth: 0,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    padding: spacing.md,
    gap: 8,
  },
  receiptBar: {
    height: 6,
    width: '42%',
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  receiptLine: {
    height: 5,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  receiptTotal: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    fontVariant: ['tabular-nums'],
    marginTop: 4,
  },
  arrowCol: {
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  arrowColDown: {
    width: '100%',
    height: 28,
  },
  chipCol: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    borderWidth: 1,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  chipDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  chipText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.text,
    flexShrink: 1,
  },
  stack: {
    gap: 8,
    padding: spacing.md,
    minHeight: 168,
  },
  shotKicker: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    flexShrink: 1,
  },
  heroFigure: {
    ...typography.display,
    fontSize: 28,
    lineHeight: 32,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  heroSub: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: 4,
  },
  holdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  holdLabel: {
    ...typography.caption,
    color: colors.textMuted,
    width: 88,
  },
  holdTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  holdFill: {
    height: 6,
    borderRadius: 3,
  },
  holdValue: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    fontVariant: ['tabular-nums'],
    width: 72,
    textAlign: 'right',
  },
  growBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    minHeight: 148,
    paddingTop: spacing.sm,
  },
  growCol: {
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  growAmount: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  growBar: {
    width: 36,
    borderRadius: 6,
    backgroundColor: colors.accent,
  },
  growYear: {
    ...typography.caption,
    color: colors.textMuted,
  },
  caption: {
    ...typography.caption,
    color: colors.textMuted,
  },
  loanLegend: {
    gap: 6,
  },
  toggle: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    overflow: 'hidden',
  },
  toggleOn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(61, 214, 164, 0.16)',
  },
  toggleOnText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  toggleOff: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  toggleOffText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  hhPair: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  hhCard: {
    flex: 1,
    gap: 4,
    padding: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  hhCardFocus: {
    borderColor: 'rgba(61, 214, 164, 0.55)',
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
  },
  hhValue: {
    ...typography.headline,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  hhValueFocus: {
    color: colors.accent,
  },
  cardTotals: {
    flexDirection: 'row',
    gap: 8,
  },
  cardTotalCell: {
    flex: 1,
    gap: 2,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  cardTotalValue: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  cardDueValue: {
    color: colors.warning,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  cardRowMain: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  cardName: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
  },
  cardDueSoon: {
    color: colors.warning,
    fontWeight: '600',
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  formLabel: {
    ...typography.caption,
    color: colors.textMuted,
  },
  formValue: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
  },
  channelRow: {
    flexDirection: 'row',
    gap: 8,
  },
  channelOn: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  channelOff: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  aiTeaser: {
    gap: 4,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  aiLine: {
    ...typography.body,
    color: colors.text,
    lineHeight: 22,
  },
  chatYou: {
    alignSelf: 'flex-end',
    maxWidth: '88%',
    backgroundColor: 'rgba(61, 214, 164, 0.14)',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  chatYouText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.text,
  },
  chatAi: {
    alignSelf: 'flex-start',
    maxWidth: '92%',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  chatAiText: {
    ...typography.caption,
    color: colors.textMuted,
    lineHeight: 18,
  },
});
